import { useQuery } from '@apollo/client'
import {
  GanttFeatureList,
  GanttFeatureRow,
  GanttHeader,
  GanttProvider,
  GanttSidebar,
  GanttSidebarItem,
  GanttTimeline,
} from '@/components/kibo-ui/gantt'
import type { GanttFeature } from '@/components/kibo-ui/gantt'
import { DriverActivityType } from '@/modules/activities/consts'
import { GET_ALL_DRIVER_ACTIVITIES } from '@/modules/activities/queries'
import { format } from '@/utils/date-fns'
import { useActivityColor } from '@/utils'
import { Loader, Modal } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { addHours, differenceInMinutes, parseISO, subHours } from 'date-fns'
import { Minus, Plus, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'

type ActivityRowKey =
  | 'drive'
  | 'work'
  | 'availability'
  | 'pause'
  | 'rest'
  | 'unavailable'

type WorkPeriodActivitiesModalProps = {
  endAt: Date | null
  onClose: () => void
  open: boolean
  startAt: Date | null
  userId: string | null
}

const ZOOM_LEVELS = [10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 75, 100, 200, 400]
const ZOOM_MIN = ZOOM_LEVELS[0]!
const ZOOM_MAX = ZOOM_LEVELS[ZOOM_LEVELS.length - 1]!

function prevZoom(z: number): number {
  for (let i = ZOOM_LEVELS.length - 1; i >= 0; i--) {
    if (ZOOM_LEVELS[i]! < z) return ZOOM_LEVELS[i]!
  }
  return ZOOM_MIN
}

function nextZoom(z: number): number {
  for (const lvl of ZOOM_LEVELS) {
    if (lvl > z) return lvl
  }
  return ZOOM_MAX
}

function activityTypeToRow(type: string): ActivityRowKey | null {
  switch (type) {
    case DriverActivityType.DRIVE:
      return 'drive'
    case DriverActivityType.WORK:
      return 'work'
    case DriverActivityType.AVAILABLE:
      return 'availability'
    case DriverActivityType.PAID_REST:
      return 'pause'
    case DriverActivityType.REST:
      return 'rest'
    case DriverActivityType.NOT_AVAILABLE:
      return 'unavailable'
    default:
      return null
  }
}

export const WorkPeriodActivitiesModal: React.FC<
  WorkPeriodActivitiesModalProps
> = ({ endAt, onClose, open, startAt, userId }) => {
  const translations = useTranslate({
    activityTimeline: 'infringements.activity-timeline',
    available: 'common.availability',
    close: 'common.close',
    driving: 'common.driving',
    loading: 'common.loading-ellipsis',
    noData: 'common.no-data',
    pause: 'common.pause',
    rest: 'common.rest',
    unavailable: 'common.unavailable',
    work: 'common.work',
    zoomIn: 'common.zoom-in',
    zoomOut: 'common.zoom-out',
  })
  const getActivityColor = useActivityColor()
  const [ganttZoom, setGanttZoom] = useState(30)

  const dateStart = startAt?.toISOString() ?? null
  const dateEnd = endAt?.toISOString() ?? null

  const { data, loading } = useQuery(GET_ALL_DRIVER_ACTIVITIES, {
    skip: !open || !userId || !dateStart || !dateEnd,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      userId: userId!,
      dateStart: dateStart!,
      dateEnd: dateEnd!,
    },
  })

  const rowLabels: Record<ActivityRowKey, string> = useMemo(
    () => ({
      drive: translations.driving,
      work: translations.work,
      availability: translations.available,
      pause: translations.pause,
      rest: translations.rest,
      unavailable: translations.unavailable,
    }),
    [
      translations.available,
      translations.driving,
      translations.pause,
      translations.rest,
      translations.unavailable,
      translations.work,
    ]
  )

  const featuresByRow = useMemo<Record<ActivityRowKey, GanttFeature[]>>(() => {
    const byRow: Record<ActivityRowKey, GanttFeature[]> = {
      drive: [],
      work: [],
      availability: [],
      pause: [],
      rest: [],
      unavailable: [],
    }

    for (const activity of data?.allFrontendDriverActivities ?? []) {
      const row = activityTypeToRow(activity.activityType)
      if (!row) continue

      const activityStart = parseISO(activity.datetimeStart)
      const activityEnd = parseISO(activity.datetimeEnd)
      const duration = differenceInMinutes(activityEnd, activityStart)

      byRow[row].push({
        id: activity.id,
        name: '',
        startAt: activityStart,
        endAt: activityEnd,
        duration,
        status: {
          id: activity.activityType,
          name: rowLabels[row],
          color: getActivityColor(activity),
        },
        tooltipContent: (
          <div>
            <h3>{rowLabels[row]}</h3>
            <ul>
              <li>
                {format(activityStart, 'HH:mm')} - {format(activityEnd, 'HH:mm')}
              </li>
              <li>
                {Math.floor(duration / 60)}h {duration % 60}m
              </li>
              {activity.vehicleRegistrationNumber && (
                <li>{activity.vehicleRegistrationNumber}</li>
              )}
            </ul>
          </div>
        ),
      })
    }

    return byRow
  }, [data?.allFrontendDriverActivities, getActivityColor, rowLabels])

  const orderedRows = useMemo<ActivityRowKey[]>(
    () =>
      (
        [
          'drive',
          'work',
          'availability',
          'pause',
          'rest',
          'unavailable',
        ] as ActivityRowKey[]
      ).filter(row => featuresByRow[row].length > 0),
    [featuresByRow]
  )

  const rowDurationMinutes = useMemo(() => {
    const durations: Record<ActivityRowKey, number> = {
      drive: 0,
      work: 0,
      availability: 0,
      pause: 0,
      rest: 0,
      unavailable: 0,
    }

    for (const row of orderedRows) {
      durations[row] = featuresByRow[row].reduce(
        (sum, feature) => sum + (feature.duration ?? 0),
        0
      )
    }

    return durations
  }, [featuresByRow, orderedRows])

  const timelineStart = useMemo(() => {
    if (!startAt) return new Date()
    const value = subHours(startAt, 1)
    value.setMinutes(0, 0, 0)
    return value
  }, [startAt])

  const timelineEnd = useMemo(() => {
    if (!endAt) return new Date()
    const value = addHours(endAt, 1)
    value.setMinutes(0, 0, 0)
    return value
  }, [endAt])

  return (
    <Modal
      open={open}
      offset={isMobile ? 8 : 36}
      placement="top-center"
      direction="from-top"
      onClose={onClose}
    >
      <div className="flex max-h-[calc(100vh-72px)] w-[min(1200px,calc(100vw-16px))] flex-col overflow-hidden rounded-xl bg-white shadow-2xl md:w-[min(1200px,calc(100vw-72px))]">
        <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-4 py-3 md:px-6">
          <div className="flex flex-col">
            <h2 className="text-base font-semibold text-gray-900">
              {translations.activityTimeline}
            </h2>
            {startAt && endAt && (
              <span className="text-xs text-gray-500">
                {format(startAt, 'PP HH:mm')} - {format(endAt, 'PP HH:mm')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 rounded-lg border border-gray-200">
              <button
                className="flex h-8 w-8 items-center justify-center text-gray-500 disabled:opacity-40"
                disabled={ganttZoom <= ZOOM_MIN}
                onClick={() => setGanttZoom(z => prevZoom(z))}
                type="button"
                aria-label={translations.zoomOut}
              >
                <Minus size={14} />
              </button>
              <span className="min-w-[36px] text-center text-[11px] font-semibold text-gray-900">
                {ganttZoom}%
              </span>
              <button
                className="flex h-8 w-8 items-center justify-center text-gray-500 disabled:opacity-40"
                disabled={ganttZoom >= ZOOM_MAX}
                onClick={() => setGanttZoom(z => nextZoom(z))}
                type="button"
                aria-label={translations.zoomIn}
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
              aria-label={translations.close}
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="overflow-y-auto px-4 py-4 md:px-6 md:py-6">
          {loading ? (
            <div className="flex min-h-48 items-center justify-center gap-3 text-sm text-gray-500">
              <Loader.Spinner />
              <span>{translations.loading}</span>
            </div>
          ) : orderedRows.length === 0 ? (
            <div className="flex min-h-48 items-center justify-center text-sm text-gray-500">
              {translations.noData}
            </div>
          ) : (
            <div className="relative w-full max-w-full pb-4">
              <GanttProvider
                className="border pb-5"
                range="daily"
                zoom={ganttZoom}
                datetimeStart={timelineStart}
                datetimeEnd={timelineEnd}
              >
                <GanttSidebar
                  className="!w-28 sm:!w-56 md:!w-64"
                  compact={isMobile}
                >
                  {orderedRows.map(row => {
                    const rowFeatures = featuresByRow[row]
                    return (
                      <GanttSidebarItem
                        key={row}
                        feature={{
                          id: row,
                          name: rowLabels[row],
                          startAt: rowFeatures[0]?.startAt ?? timelineStart,
                          endAt:
                            rowFeatures[rowFeatures.length - 1]?.endAt ??
                            timelineEnd,
                          duration: rowDurationMinutes[row],
                        }}
                      />
                    )
                  })}
                </GanttSidebar>
                <GanttTimeline>
                  <GanttHeader />
                  <GanttFeatureList>
                    {orderedRows.map(row => (
                      <GanttFeatureRow
                        key={row}
                        className="min-w-full"
                        features={featuresByRow[row]}
                        handleViewFeature={() => void 0}
                      />
                    ))}
                  </GanttFeatureList>
                </GanttTimeline>
              </GanttProvider>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
